import { useMemo, useState } from 'react';
import Modal from '@/components/common/Modal';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { useGetDepositAccountsQuery } from '@/store/api/accountApi';
import { setDepositModalOpen } from '@/store/slices/uiSlice';
import { getAvailableBalance } from '@/utils';
import { Icons } from '@/utils/Icons';
import { useTranslation } from '@/i18n';
import DepositAmountStep from './DepositAmountStep';
import DepositMethodList from './DepositMethodList';
import DepositPaymentStep from './DepositPaymentStep';
import {
  buildDepositOptions,
  type DepositOption,
  formatInr,
  getAmountRange,
  getOptionsForAmount,
} from './depositOptions';

type Step = 'amount' | 'method' | 'payment';

/** Mounted only while open, so closing the popup always starts the next deposit fresh. */
const DepositFlow = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('amount');
  const [amount, setAmount] = useState('');
  const [option, setOption] = useState<DepositOption | null>(null);

  // The same accounts list the /deposit page uses.
  const { data, isLoading, isError } = useGetDepositAccountsQuery();
  const allOptions = useMemo(() => buildDepositOptions(data), [data]);
  const amountValue = Number(amount);
  // Only the accounts whose min / max accept the amount.
  const options = useMemo(
    () => getOptionsForAmount(allOptions, amountValue),
    [allOptions, amountValue]
  );
  const range = useMemo(() => getAmountRange(allOptions), [allOptions]);

  const balance = getAvailableBalance(useAppSelector((state) => state.user.walletInfo));

  const goBack = step === 'payment' ? 'method' : step === 'method' ? 'amount' : null;

  const title = (
    <span className="flex items-center gap-3">
      {goBack && (
        <button
          type="button"
          onClick={() => setStep(goBack)}
          aria-label="Back"
          className="-ml-1 flex size-8 items-center justify-center rounded-full text-shell-muted transition hover:bg-shell-raised hover:text-shell-fg"
        >
          <Icons.previous />
        </button>
      )}
      {step === 'amount' ? t('common.deposit') : `${t('common.deposit')} ${formatInr(amountValue)}`}
    </span>
  );

  return (
    <Modal open onClose={onClose} title={title} size="sm">
      {step === 'amount' && (
        <DepositAmountStep
          balance={balance}
          amount={amount}
          onAmountChange={setAmount}
          range={range}
          onNext={() => setStep('method')}
        />
      )}
      {step === 'method' && (
        <DepositMethodList
          options={options}
          isLoading={isLoading}
          isError={isError}
          onSelect={(selected) => {
            setOption(selected);
            setStep('payment');
          }}
        />
      )}
      {step === 'payment' && option && (
        <DepositPaymentStep option={option} amount={amountValue} onSubmitted={onClose} />
      )}
    </Modal>
  );
};

/**
 * Deposit popup, opened by the header's Deposit button (`ui.depositModalOpen`):
 * coins → the UPI / bank / crypto accounts whose limits accept the amount → pay and submit.
 * The /deposit page is separate and unchanged.
 */
const DepositModal = () => {
  const dispatch = useAppDispatch();
  const open = useAppSelector((state) => state.ui.depositModalOpen);
  if (!open) return null;
  return <DepositFlow onClose={() => dispatch(setDepositModalOpen(false))} />;
};

export default DepositModal;
